/**
 * Marketing / ad-attribution query parameters.
 *
 * Single source of truth for the params we treat as campaign attribution.
 * Used to (a) persist signup attribution on the login page and (b) preserve
 * attribution on PostHog `$pageview` URLs.
 *
 * This is an allowlist on purpose: pageview URLs must NOT carry sensitive query
 * params such as OAuth `code`/`state`, `installation_id`, or `token` into
 * PostHog (events + session replay). Only the keys below are ever preserved.
 */
export const TRACKING_PARAM_KEYS = [
  // UTM parameters
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  // Google Ads
  'gclid',
  'gclsrc',
  'dclid',
  'wbraid',
  'gbraid',
  // Facebook/Meta
  'fbclid',
  // Microsoft/Bing
  'msclkid',
  // TikTok
  'ttclid',
  // LinkedIn
  'li_fat_id',
  // Twitter/X
  'twclid',
  // General
  'ref',
  'source',
] as const;

export const SIGNUP_TRACKING_STORAGE_KEY = 'signup_tracking_params';

export type SignupTrackingParams = Record<string, string>;

const MAX_ATTRIBUTION_VALUE_LENGTH = 500;

function safeStorageRead(): SignupTrackingParams {
  try {
    const stored = sessionStorage.getItem(SIGNUP_TRACKING_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object'
      ? parsed as SignupTrackingParams
      : {};
  } catch {
    return {};
  }
}

/**
 * Capture first-touch signup attribution before an OAuth redirect.
 *
 * This is intentionally callable from every page, not only `/login`: most
 * marketing CTAs start GitHub OAuth directly. Existing values win so a later
 * navigation without UTMs cannot erase the visitor's original source.
 *
 * `markDirectFallback` stamps `utm_source=direct` when nothing else identifies
 * the visit. Pass it ONLY at the OAuth handoff, never on a pageview: capture is
 * once-only and `sessionStorage` spans the whole tab, so an early "direct" stamp
 * would outrank an ad click that lands later in that same tab and would report
 * paid signups as organic.
 */
export function captureSignupTrackingParams(
  search?: string,
  options?: { markDirectFallback?: boolean }
): SignupTrackingParams {
  if (typeof window === 'undefined') return {};

  const captured = safeStorageRead();
  const input = new URLSearchParams(
    (search ?? window.location.search).replace(/^\?/, '')
  );

  for (const key of TRACKING_PARAM_KEYS) {
    const value = input.get(key)?.trim();
    if (value && !captured[key]) {
      captured[key] = value.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH);
    }
  }

  if (!captured.first_landing_page) {
    captured.first_landing_page = window.location.pathname.slice(
      0,
      MAX_ATTRIBUTION_VALUE_LENGTH
    );
  }

  if (!captured.referring_domain) {
    try {
      const referrer = document.referrer ? new URL(document.referrer) : null;
      if (referrer && referrer.origin !== window.location.origin) {
        captured.referring_domain = referrer.hostname.slice(
          0,
          MAX_ATTRIBUTION_VALUE_LENGTH
        );
      }
    } catch {
      // Invalid or unavailable referrer is equivalent to a direct visit.
    }
  }

  // "Direct" is a conclusion, not a default: only claim it when no campaign param,
  // no ad click id, and no external referrer was ever seen in this tab.
  const hasAnyKnownSource =
    TRACKING_PARAM_KEYS.some((key) => Boolean(captured[key])) ||
    Boolean(captured.referring_domain);
  if (options?.markDirectFallback && !hasAnyKnownSource) {
    captured.utm_source = 'direct';
    captured.utm_medium = 'none';
  }

  try {
    sessionStorage.setItem(
      SIGNUP_TRACKING_STORAGE_KEY,
      JSON.stringify(captured)
    );
  } catch {
    // Attribution is best-effort when storage is unavailable.
  }

  return captured;
}

export function getSignupTrackingParams(): SignupTrackingParams {
  return safeStorageRead();
}

export function clearSignupTrackingParams(): void {
  try {
    sessionStorage.removeItem(SIGNUP_TRACKING_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in hardened/private browsers.
  }
}

/**
 * Given a raw URL query string, return a query string containing ONLY the
 * allowlisted marketing/attribution params, with a leading `?`. Returns an
 * empty string when none are present.
 *
 * Accepts the query string with or without a leading `?`.
 */
export function preserveTrackingParams(search: string): string {
  const input = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const kept = new URLSearchParams();

  for (const key of TRACKING_PARAM_KEYS) {
    const value = input.get(key);
    if (value) {
      kept.set(key, value);
    }
  }

  const result = kept.toString();
  return result ? `?${result}` : '';
}
